import { useEffect, useMemo, useState } from "react";
import ReportService from "../../../services/reports/Report.service";
import YoungworldReportService from "../../../services/reports/YoungworldReport.service";
import EpochMonthService from "../../../services/EpochMonth.service";
import MonthSelector from "../../../tailwindComponents/ui/dates/month-selector.component";
import ReportTable from "../ReportTable.component";
import * as XLSX from "xlsx";
import { WeeklyRawData } from "./YoungWorldTypes";
import { BaseReportParsedData, MoneyDataItem } from "../../../interfaces/reports/BaseReport.interface";
import type { EmployeeFilter } from "../../../services/reports/YoungworldReport.service";

interface YoungworldWeeklyReportProps {
    clientId: string | number;
    epochMonth?: number | null;
}

const HIERARCHY_OPTIONS = {
    "Company": ["category", "locationCode"] as const,
    "By Location": ["locationCode"] as const,
    "By Category": ["category"] as const,
    "Category by Class by Location": ["category", "classCode", "locationCode"] as const,
  };

function num(v: any): number { return (v === undefined || v === null || isNaN(Number(v))) ? 0 : Number(v); }

function monthLabel(epoch: number): string {
    const full = EpochMonthService.epochMonthToMMMM(epoch);
    return full.substring(0, 3);
}

// Helper to get quarter start for a given epoch month (current quarter)
function getQuarterStart(epochMonth: number): number {
    const date = EpochMonthService.epochMonthToDate(epochMonth);
    const m = date.getMonth() + 1;
    let year = date.getFullYear();
    let quarterMonth: number;
    if ([12, 1, 2].includes(m)) { quarterMonth = 12; if (m !== 12) year -= 1; }
    else if ([3, 4, 5].includes(m)) { quarterMonth = 3; }
    else if ([6, 7, 8].includes(m)) { quarterMonth = 6; }
    else { quarterMonth = 9; }
    return EpochMonthService.dateToEpochMonth(new Date(year, quarterMonth - 1, 1));
}

interface WeeklyParsedData extends BaseReportParsedData {
    categorySortOrder?: number | null;
    // Month 1
    month1SalesThreeAgo: MoneyDataItem;
    month1SalesLastYear: MoneyDataItem;
    month1SalesPlan: MoneyDataItem;
    month1Otr: MoneyDataItem;
    month1Mtr: MoneyDataItem;
    month1EomPlan: MoneyDataItem;
    month1CurrentSales: MoneyDataItem;
    month1SalesTrend: MoneyDataItem; // stored as numeric but wrapped for grouping compatibility
    month1InvOh: MoneyDataItem;
    month1OnOrder: MoneyDataItem;
    month1OnOrderMtd: MoneyDataItem;
    month1Otb: MoneyDataItem;
    month1OtbUpdated: MoneyDataItem;
    month1OtbTrend: MoneyDataItem;
    month1MarkdownsRemaining: MoneyDataItem;
    // Month 2
    month2SalesThreeAgo: MoneyDataItem;
    month2SalesLastYear: MoneyDataItem;
    month2SalesPlan: MoneyDataItem;
    month2Otr: MoneyDataItem;
    month2Mtr: MoneyDataItem;
    month2EomPlan: MoneyDataItem;
    month2CurrentSales: MoneyDataItem;
    month2SalesTrend: MoneyDataItem;
    month2InvOh: MoneyDataItem;
    month2OnOrder: MoneyDataItem;
    month2OnOrderMtd: MoneyDataItem;
    month2Otb: MoneyDataItem;
    month2OtbUpdated: MoneyDataItem;
    month2OtbTrend: MoneyDataItem;
    month2MarkdownsRemaining: MoneyDataItem;
    // Month 3
    month3SalesThreeAgo: MoneyDataItem;
    month3SalesLastYear: MoneyDataItem;
    month3SalesPlan: MoneyDataItem;
    month3Otr: MoneyDataItem;
    month3Mtr: MoneyDataItem;
    month3EomPlan: MoneyDataItem;
    month3CurrentSales: MoneyDataItem;
    month3SalesTrend: MoneyDataItem;
    month3InvOh: MoneyDataItem;
    month3OnOrder: MoneyDataItem;
    month3OnOrderMtd: MoneyDataItem;
    month3Otb: MoneyDataItem;
    month3OtbUpdated: MoneyDataItem;
    month3OtbTrend: MoneyDataItem;
    month3MarkdownsRemaining: MoneyDataItem;
}

function parseWeeklyRaw(rows: WeeklyRawData[], monthEpochs: number[]): WeeklyParsedData[] {
    const [m1Epoch, m2Epoch, m3Epoch] = monthEpochs;
    const map = new Map<string, { months: Record<number, WeeklyRawData> }>();
    rows.forEach(r => {
        const key = `${r.locationCode}|${r.classCode}|${r.category || ''}`;
        if (!map.has(key)) map.set(key, { months: {} });
        map.get(key)!.months[r.epochMonthSpan] = r;
    });

    const parsed: WeeklyParsedData[] = [];
    map.forEach(entry => {
        const m1 = entry.months[m1Epoch];
        const m2 = entry.months[m2Epoch];
        const m3 = entry.months[m3Epoch];
        const first = m1 || m2 || m3;
        if (!first) return;

        const numOr0 = (v: any) => (v === undefined || v === null || isNaN(Number(v))) ? 0 : Number(v);

        parsed.push({
            locationCode: first.locationCode,
            locationName: first.locationName,
            classCode: first.classCode,
            className: first.className,
            category: first.category ?? 'N/A',
            categorySortOrder: (first as any).categorySortOrder ?? null,
            parentClientClassCode: null,
            trend: first.trend || 1,
            // m1
            month1SalesThreeAgo: new MoneyDataItem(numOr0(m1?.salesThreeAgo)),
            month1SalesLastYear: new MoneyDataItem(numOr0(m1?.salesLastYear)),
            month1SalesPlan: new MoneyDataItem(numOr0(m1?.salesPlan)),
            month1Otr: new MoneyDataItem(numOr0(m1?.otr)),
            month1Mtr: new MoneyDataItem(numOr0(m1?.mtr)),
            month1EomPlan: new MoneyDataItem(numOr0(m1?.eomInventoryPlanNextMonthBom)),
            month1CurrentSales: new MoneyDataItem(numOr0(m1?.currentSales)),
            month1SalesTrend: new MoneyDataItem(numOr0(m1?.salesTrend)),
            month1InvOh: new MoneyDataItem(numOr0(m1?.invOh)),
            month1OnOrder: new MoneyDataItem(numOr0(m1?.onOrder)),
            month1OnOrderMtd: new MoneyDataItem(numOr0(m1?.onOrderMtd)),
            month1Otb: new MoneyDataItem(numOr0(m1?.openToBuy)),
            month1OtbUpdated: new MoneyDataItem(numOr0(m1?.otbUpdated)),
            month1OtbTrend: new MoneyDataItem(ReportService.calculateTrendedOtb({
                invBomNM: numOr0(m1?.eomInventoryPlanNextMonthBom),
                currInv: numOr0(m1?.invOh),
                sales: numOr0(m1?.currentSales),
                salesPlan: numOr0(m1?.salesPlan),
                mdRemaining: numOr0(m1?.markdownsRemaining),
                onOrder: numOr0(m1?.onOrderMtd),
                trend: (m1?.trend || 1),
                format: false,
            })),
            month1MarkdownsRemaining: new MoneyDataItem(numOr0(m1?.markdownsRemaining)),
            // m2
            month2SalesThreeAgo: new MoneyDataItem(numOr0(m2?.salesThreeAgo)),
            month2SalesLastYear: new MoneyDataItem(numOr0(m2?.salesLastYear)),
            month2SalesPlan: new MoneyDataItem(numOr0(m2?.salesPlan)),
            month2Otr: new MoneyDataItem(numOr0(m2?.otr)),
            month2Mtr: new MoneyDataItem(numOr0(m2?.mtr)),
            month2EomPlan: new MoneyDataItem(numOr0(m2?.eomInventoryPlanNextMonthBom)),
            month2CurrentSales: new MoneyDataItem(numOr0(m2?.currentSales)),
            month2SalesTrend: new MoneyDataItem(numOr0(m2?.salesTrend)),
            month2InvOh: new MoneyDataItem(numOr0(m2?.invOh)),
            month2OnOrder: new MoneyDataItem(numOr0(m2?.onOrder)),
            month2OnOrderMtd: new MoneyDataItem(numOr0(m2?.onOrderMtd)),
            month2Otb: new MoneyDataItem(numOr0(m2?.openToBuy)),
            month2OtbUpdated: new MoneyDataItem(numOr0(m2?.otbUpdated)),
            month2OtbTrend: new MoneyDataItem(ReportService.calculateTrendedOtb({
                invBomNM: numOr0(m2?.eomInventoryPlanNextMonthBom),
                currInv: numOr0(m2?.invOh),
                sales: numOr0(m2?.currentSales),
                salesPlan: numOr0(m2?.salesPlan),
                mdRemaining: numOr0(m2?.markdownsRemaining),
                onOrder: numOr0(m2?.onOrderMtd),
                trend: (m2?.trend || 1),
                format: false,
            })),
            month2MarkdownsRemaining: new MoneyDataItem(numOr0(m2?.markdownsRemaining)),
            // m3
            month3SalesThreeAgo: new MoneyDataItem(numOr0(m3?.salesThreeAgo)),
            month3SalesLastYear: new MoneyDataItem(numOr0(m3?.salesLastYear)),
            month3SalesPlan: new MoneyDataItem(numOr0(m3?.salesPlan)),
            month3Otr: new MoneyDataItem(numOr0(m3?.otr)),
            month3Mtr: new MoneyDataItem(numOr0(m3?.mtr)),
            month3EomPlan: new MoneyDataItem(numOr0(m3?.eomInventoryPlanNextMonthBom)),
            month3CurrentSales: new MoneyDataItem(numOr0(m3?.currentSales)),
            month3SalesTrend: new MoneyDataItem(numOr0(m3?.salesTrend)),
            month3InvOh: new MoneyDataItem(numOr0(m3?.invOh)),
            month3OnOrder: new MoneyDataItem(numOr0(m3?.onOrder)),
            month3OnOrderMtd: new MoneyDataItem(numOr0(m3?.onOrderMtd)),
            month3Otb: new MoneyDataItem(numOr0(m3?.openToBuy)),
            month3OtbUpdated: new MoneyDataItem(numOr0(m3?.otbUpdated)),
            month3OtbTrend: new MoneyDataItem(ReportService.calculateTrendedOtb({
                invBomNM: numOr0(m3?.eomInventoryPlanNextMonthBom),
                currInv: numOr0(m3?.invOh),
                sales: numOr0(m3?.currentSales),
                salesPlan: numOr0(m3?.salesPlan),
                mdRemaining: numOr0(m3?.markdownsRemaining),
                onOrder: numOr0(m3?.onOrderMtd),
                trend: (m3?.trend || 1),
                format: false,
            })),
            month3MarkdownsRemaining: new MoneyDataItem(numOr0(m3?.markdownsRemaining)),
        });
    });

    return parsed;
}

const YoungworldWeeklyReport = ({ clientId, epochMonth }: YoungworldWeeklyReportProps) => {
    const [selectedEpochMonth, setSelectedEpochMonth] = useState<number>(epochMonth ?? EpochMonthService.getTodayEpochMonth());

    const maxSelectableEpochMonth = useMemo(() => EpochMonthService.getTodayEpochMonth() + 1, []);

    // Clamp to max month allowed (1 month ahead of today)
    useEffect(() => {
        if (selectedEpochMonth > maxSelectableEpochMonth) {
            setSelectedEpochMonth(maxSelectableEpochMonth);
        }
    }, [selectedEpochMonth, maxSelectableEpochMonth]);

    const [loading, setLoading] = useState<boolean>(false);
    const [hierarchyKey, setHierarchyKey] = useState<keyof typeof HIERARCHY_OPTIONS>("Company");
    const [employee, setEmployee] = useState<EmployeeFilter>('none');
    const hierarchy = HIERARCHY_OPTIONS[hierarchyKey] as readonly string[];

    const quarterStart = useMemo(() => getQuarterStart(selectedEpochMonth), [selectedEpochMonth]);
    const monthEpochs = useMemo(() => [quarterStart, quarterStart + 1, quarterStart + 2], [quarterStart]);

    const [parsed, setParsed] = useState<WeeklyParsedData[]>([]);

    useEffect(() => {
        const load = async () => {
        setLoading(true);
        try {
            const data = await YoungworldReportService.getWeeklyReportData({ clientId, epochMonth: selectedEpochMonth });
            const rows: WeeklyRawData[] = Array.isArray((data as any)?.data) ? (data as any).data : (data as WeeklyRawData[]);
            const p = parseWeeklyRaw(rows || [], monthEpochs);
            setParsed(p);
        } catch (e) {
            console.error(e);
            setParsed([]);
        } finally {
            setLoading(false);
        }
        };
        load();
    }, [clientId, selectedEpochMonth, monthEpochs]);

    // Default hierarchy to Category by Class by Location when an employee filter is active
    useEffect(() => {
        if (employee !== 'none') {
            setHierarchyKey('Category by Class by Location');
        }
    }, [employee]);

    const filtered = useMemo(() => {
        const classCodes = YoungworldReportService.getEmployeeClassCodes(employee);
        const employeeFiltered = (classCodes.length > 0) ? parsed.filter(p => classCodes.includes(Number(p.classCode))) : parsed;
        return ReportService.sortAndFilterData(employeeFiltered, [...hierarchy], [], [], []);
    }, [parsed, hierarchy, employee]);

    const columns = useMemo(() => {
        const hierarchyColumns = hierarchy.map(h => ({ name: h === 'locationCode' ? 'Location' : h === 'category' ? 'Category' : h === 'classCode' ? 'Class' : h, sortable: false }));
        const mLabels = monthEpochs.map(e => monthLabel(e));
        const isFuture = (epoch: number) => epoch > selectedEpochMonth;
        const isTarget = (epoch: number) => epoch === selectedEpochMonth;

        const buildMonthCols = (label: string, epoch: number, future: boolean, target: boolean) => {
            if (target) {
                return [
                    // { name: `${label} 3Ago`, sortable: false },
                    { name: `${label} LY`, sortable: false },
                    { name: `${label} Plan`, sortable: false },
                    { name: `${label} MTD`, sortable: false },
                    { name: `${label} Sales Trend`, sortable: false },
                    { name: `Inv OH.`, sortable: false },
                    { name: `${label} OTR`, sortable: false },
                    { name: `${label} MTR`, sortable: false },
                    { name: `${label} OO`, sortable: false },
                    { name: `${label} EOM Plan`, sortable: false },
                    { name: `${label} OTB`, sortable: false },
                    { name: `${label} OTB Updated`, sortable: false },
                    { name: `${label} OTB Trended`, sortable: false },
                ]
            }
            if (!future && !target) {
                return [
                    // { name: `${label} 3Ago`, sortable: false },
                    { name: `${label} LY`, sortable: false },
                    { name: `${label} Plan`, sortable: false },
                    { name: `${label} Sales`, sortable: false },
                    { name: `${label} OTR`, sortable: false },
                    { name: `${label} OO`, sortable: false },
                    { name: `${label} EOM Plan`, sortable: false },
                ];
            }
            return [
                // { name: `${label} 3Ago`, sortable: false },
                { name: `${label} LY`, sortable: false },
                { name: `${label} Plan`, sortable: false },
                { name: `${label} OTR`, sortable: false },
                { name: `${label} OO`, sortable: false },
                { name: `${label} EOM Plan`, sortable: false },
            ]
        };

        const cols: { name: string; sortable: boolean }[] = [
            ...hierarchyColumns,
            ...buildMonthCols(mLabels[0], monthEpochs[0], isFuture(monthEpochs[0]), isTarget(monthEpochs[0])),
            ...buildMonthCols(mLabels[1], monthEpochs[1], isFuture(monthEpochs[1]), isTarget(monthEpochs[1])),
            ...buildMonthCols(mLabels[2], monthEpochs[2], isFuture(monthEpochs[2]), isTarget(monthEpochs[2])),
        ];
        return cols;
    }, [hierarchy, monthEpochs, selectedEpochMonth]);

    const tableData = useMemo(() => {
        const isFuture = (epoch: number) => epoch > selectedEpochMonth;
        const isTarget = (epoch: number) => epoch === selectedEpochMonth;
        return filtered.map((r: WeeklyParsedData) => {
            const labels = ReportService.hierarchyItemToVerticalDisplayNames(r, [...hierarchy]);

            const m1Row = (() => {
                const future = isFuture(monthEpochs[0]);
                const target = isTarget(monthEpochs[0]);
                // common cols
                const cols = [];
                cols.push(r.month1SalesLastYear.formatted);
                cols.push(r.month1SalesPlan.formatted);
                if (target) {
                    cols.push(r.month1CurrentSales.formatted);
                    cols.push(r.month1SalesTrend.formatted);
                    cols.push(r.month1InvOh.formatted);
                }
                if (!future && !target) {
                    cols.push(r.month1CurrentSales.formatted);
                }
                cols.push(r.month1Otr.formatted);
                if (target) {
                    cols.push(r.month1Mtr.formatted);
                }
                cols.push(target ? r.month1OnOrderMtd.formatted : r.month1OnOrder.formatted);
                cols.push(r.month1EomPlan.formatted);
                if (target) {
                    cols.push(r.month1Otb.formatted);
                    cols.push(r.month1OtbUpdated.formatted);
                    cols.push(r.month1OtbTrend.formatted);
                }
                return cols;
            })();

            const m2Row = (() => {
                const future = isFuture(monthEpochs[1]);
                const target = isTarget(monthEpochs[1]);

                // 3 common cols
                const cols = [];
                cols.push(r.month2SalesLastYear.formatted);
                cols.push(r.month2SalesPlan.formatted);
                if (target) {
                    cols.push(r.month2CurrentSales.formatted);
                    cols.push(r.month2SalesTrend.formatted);
                    cols.push(r.month2InvOh.formatted);
                }
                if (!future && !target) {
                    cols.push(r.month2CurrentSales.formatted);
                }
                cols.push(r.month2Otr.formatted);
                if (target) {
                    cols.push(r.month2Mtr.formatted);
                }
                cols.push(target ? r.month2OnOrderMtd.formatted : r.month2OnOrder.formatted);
                cols.push(r.month2EomPlan.formatted);
                if (target) {
                    cols.push(r.month2Otb.formatted);
                    cols.push(r.month2OtbUpdated.formatted);
                    cols.push(r.month2OtbTrend.formatted);
                }
                return cols;
            })();

            const m3Row = (() => {
                const future = isFuture(monthEpochs[2]);
                const target = isTarget(monthEpochs[2]);

                // 3 common cols
                const cols = [];
                cols.push(r.month3SalesLastYear.formatted);
                cols.push(r.month3SalesPlan.formatted);
                if (target) {
                    cols.push(r.month3CurrentSales.formatted);
                    cols.push(r.month3SalesTrend.formatted);
                    cols.push(r.month3InvOh.formatted);
                }
                if (!future && !target) {
                    cols.push(r.month3CurrentSales.formatted);
                }
                cols.push(r.month3Otr.formatted);
                if (target) {
                    cols.push(r.month3Mtr.formatted);
                }
                cols.push(target ? r.month3OnOrderMtd.formatted : r.month3OnOrder.formatted);
                cols.push(r.month3EomPlan.formatted);
                if (target) {
                    cols.push(r.month3Otb.formatted);
                    cols.push(r.month3OtbUpdated.formatted);
                    cols.push(r.month3OtbTrend.formatted);
                }
                return cols;
            })();
            
            return [
                ...labels,
                ...m1Row,
                ...m2Row,
                ...m3Row,
            ];
        });
    }, [filtered, hierarchy, monthEpochs, selectedEpochMonth]);

    const exportToExcel = () => {
        try {
            const header = columns.map(c => c.name);
            const ws = XLSX.utils.aoa_to_sheet([header, ...tableData]);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'YoungworldWeekly');
            const quarterLabel = `${monthLabel(monthEpochs[0])}-${monthLabel(monthEpochs[2])}`;
            const fileName = `Youngworld_Weekly_${quarterLabel}_${EpochMonthService.epochMonthToMMMYY(selectedEpochMonth)}.xlsx`;
            XLSX.writeFile(wb, fileName);
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <div className="w-full h-full flex flex-col items-center justify-center text-center max-w-[1700px]">
        <h2 className="text-2xl font-bold mb-2">
            {`${EpochMonthService.epochMonthToMMMYY(selectedEpochMonth)} Youngworld Weekly (${monthLabel(monthEpochs[0])}-${monthLabel(monthEpochs[2])}) - ${hierarchyKey}`}
        </h2>
        <div className="w-full flex flex-wrap items-center justify-end pr-4 mb-2 gap-2">
            <div className="flex items-center gap-2 mr-auto pl-2">
                <label className="text-sm">Month:</label>
                <MonthSelector
                    epochMonth={selectedEpochMonth}
                    setEpochMonth={(v: number) => setSelectedEpochMonth(Math.min(v, maxSelectableEpochMonth))}
                    maxMonth={maxSelectableEpochMonth}
                />
            </div>
            <div className="flex items-center gap-2">
                <label className="text-sm">Hierarchy:</label>
                <select
                    className="h-9 px-2 border rounded-md text-sm"
                    value={hierarchyKey}
                    onChange={(e) => setHierarchyKey(e.target.value as keyof typeof HIERARCHY_OPTIONS)}
                >
                    {Object.keys(HIERARCHY_OPTIONS).map(k => <option key={k} value={k}>{k}</option>)}
                </select>
            </div>
            <div className="flex items-center gap-2">
                <label className="text-sm">Employee:</label>
                <select
                    className="h-9 px-2 border rounded-md text-sm"
                    value={employee}
                    onChange={(e) => setEmployee(e.target.value as EmployeeFilter)}
                >
                    <option value="none">All</option>
                    <option value="Lisa">Lisa</option>
                    <option value="Hector">Hector</option>
                    <option value="Aaron">Aaron</option>
                </select>
            </div>
            <button
                onClick={exportToExcel}
                className="h-9 px-4 rounded-md text-[13px] font-semibold text-white bg-tw-primary hover:bg-sky-600 shadow transition"
            >
                Download Excel
            </button>
        </div>
        {loading ? (
            <p>Loading...</p>
        ) : (
            <div className="w-full h-full">
                <ReportTable
                    columns={columns}
                    data={tableData}
                    headerClassName="bg-slate-700 !text-white !text-center py-2 px-4"
                    stickyHeader={true}
                />
            </div>
        )}
        </div>
    );
};

export default YoungworldWeeklyReport;